import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import EditIcon from '@mui/icons-material/EditRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import FieldWithActions from '@components/FieldWithActions';
import CircularProgress from '@mui/material/CircularProgress';
import { CssTextFieldOutlined, CssMenuItem, CssMenuItemCreatable } from '@components/Select';
import { Typography } from '@components/Typography';
import { Switch } from '@components/Switch';
import { useTranslation } from '@hooks';
import { alertActions } from '@actions/alert';
import usePermissions from '@hooks/usePermissions';
import { micrositesService } from '@services';
import { usePopupZIndex } from '@components/Modal/popupLayer';
import StatusDot from '@components/StatusDot';
import AddDomainModal from './AddDomainModal';
import EditDomainModal from './EditDomainModal';

const Wrapper = styled('div')({
    paddingTop: 16,
});

const StyledSwitch = styled(Switch)({
    paddingTop: 8,
    width: '100%',
});

const DomainRow = styled('span')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
});

const DomainName = styled('span')({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const CustomDomainSection = ({ customDomainId, isView, onDomainChange, onHostnameChange }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const popupZIndex = usePopupZIndex();
    const { hasFullSettings, hasFullAccess } = usePermissions();
    const isSuperAdmin = hasFullSettings();
    const isUser = !hasFullAccess('microsites');

    const STATUS_LABELS = {
        0: t('MicrositesPage.statusPending'),
        1: t('MicrositesPage.statusActive'),
        2: t('MicrositesPage.statusFailed'),
    };

    const [enabled, setEnabled] = useState(!!customDomainId);
    const [domains, setDomains] = useState([]);
    const [domainsLoaded, setDomainsLoaded] = useState(false);
    const [selectedDomainId, setSelectedDomainId] = useState(customDomainId ?? '');
    const [open, setOpen] = useState(false);

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editDomain, setEditDomain] = useState(null);

    // Sync local state when prop changes (e.g. modal reopened for different microsite)
    useEffect(() => {
        setEnabled(!!customDomainId);
        setSelectedDomainId(customDomainId ?? '');
    }, [customDomainId]);

    useEffect(() => {
        let cancelled = false;
        if (enabled && !domainsLoaded) {
            (async () => {
                try {
                    const data = await micrositesService.getCustomDomains();
                    if (!cancelled) setDomains(Array.isArray(data) ? data : []);
                } catch (err) {
                    if (cancelled) return;
                    setDomains([]);
                    if (err?._serverError) return;
                    dispatch(alertActions.error(err?.message || t('MicrositesPage.loadError')));
                } finally {
                    if (!cancelled) setDomainsLoaded(true);
                }
            })();
        }
        return () => {
            cancelled = true;
        };
    }, [enabled, domainsLoaded, dispatch, t]);

    const selectedDomain = domains.find(d => d.id === selectedDomainId);
    const cantInteract = isView || isUser;

    // Only show custom domain in URL field when domain is active
    const activeHostname = selectedDomain?.status === 1 ? selectedDomain.hostname : null;
    useEffect(() => {
        onHostnameChange?.(activeHostname);
    }, [activeHostname, onHostnameChange]);

    const handleToggle = event => {
        const newEnabled = event.target.checked;
        if (!newEnabled) {
            setSelectedDomainId('');
            onDomainChange(null);
        }
        setEnabled(newEnabled);
    };

    const handleDomainSelect = event => {
        setSelectedDomainId(event.target.value);
        onDomainChange(event.target.value || null);
    };

    const handleDomainAdded = newDomain => {
        setDomains(prev => [...prev, newDomain]);
        setSelectedDomainId(newDomain.id);
        onDomainChange(newDomain.id);
    };

    const handleDomainUpdated = updatedDomain => {
        setDomains(prev => prev.map(d => (d.id === updatedDomain.id ? updatedDomain : d)));
        setEditDomain(updatedDomain);
    };

    const handleDomainDeleted = deletedId => {
        setDomains(prev => prev.filter(d => d.id !== deletedId));
        if (selectedDomainId === deletedId) {
            setSelectedDomainId('');
            setEnabled(false);
            onDomainChange(null);
        }
        setEditDomain(null);
        dispatch(alertActions.success(t('MicrositesPage.domainDeleted')));
    };

    return (
        <Wrapper>
            <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', paddingBottom: '4px' }}>
                {t('MicrositesPage.customDomain')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', paddingBottom: '4px' }}>
                {t('MicrositesPage.customDomainCaption')}
            </Typography>
            <StyledSwitch
                checked={enabled}
                handleChange={handleToggle}
                label={t('MicrositesPage.useCustomDomain')}
                disabled={cantInteract}
            />

            {enabled && (
                <div style={{ marginTop: 8 }}>
                    {!domainsLoaded ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0 16px' }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                                Loading...
                            </Typography>
                        </div>
                    ) : (
                        <FieldWithActions
                            actions={
                                isSuperAdmin && selectedDomain
                                    ? [
                                          {
                                              icon: <EditIcon />,
                                              label: t('buttons.edit'),
                                              onClick: () => setEditDomain(selectedDomain),
                                          },
                                      ]
                                    : []
                            }
                        >
                            <CssTextFieldOutlined
                                select
                                fullWidth
                                label={t('MicrositesPage.domain')}
                                value={selectedDomainId ?? ''}
                                onChange={handleDomainSelect}
                                disabled={cantInteract}
                                slotProps={{
                                    select: {
                                        open,
                                        onOpen: () => setOpen(true),
                                        onClose: () => setOpen(false),
                                        MenuProps: {
                                            style: { zIndex: popupZIndex },
                                            slotProps: { paper: { style: { maxHeight: '55vh', maxWidth: 400 } } },
                                        },
                                        renderValue: value => {
                                            const d = domains.find(x => x.id === value);
                                            if (!d) return '';
                                            return (
                                                <DomainRow>
                                                    <StatusDot status={d.status} />
                                                    <DomainName>{d.hostname}</DomainName>
                                                </DomainRow>
                                            );
                                        },
                                    },
                                }}
                            >
                                {domains.length === 0 && (
                                    <CssMenuItem disabled>
                                        {isSuperAdmin
                                            ? t('MicrositesPage.noDomainsInAccount')
                                            : t('MicrositesPage.noDomainsAvailable')}
                                    </CssMenuItem>
                                )}

                                {domains.map(domain => (
                                    <CssMenuItem key={domain.id} value={domain.id}>
                                        <DomainRow>
                                            <StatusDot status={domain.status} />
                                            <DomainName>{domain.hostname}</DomainName>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'var(--text-secondary)', ml: 0.5, whiteSpace: 'nowrap' }}
                                            >
                                                {STATUS_LABELS[domain.status]}
                                            </Typography>
                                        </DomainRow>
                                    </CssMenuItem>
                                ))}

                                {isSuperAdmin && domains.length > 0 && <Divider />}

                                {isSuperAdmin && (
                                    <CssMenuItemCreatable
                                        onClick={e => {
                                            e.preventDefault();
                                            setOpen(false);
                                            setAddModalOpen(true);
                                        }}
                                    >
                                        <AddIcon />
                                        <Typography sx={{ color: 'var(--primary-main)' }} variant="body2">
                                            {t('MicrositesPage.addNewDomain')}
                                        </Typography>
                                    </CssMenuItemCreatable>
                                )}
                            </CssTextFieldOutlined>
                        </FieldWithActions>
                    )}
                </div>
            )}

            <AddDomainModal
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onDomainAdded={handleDomainAdded}
            />
            <EditDomainModal
                open={!!editDomain}
                onClose={() => setEditDomain(null)}
                domain={editDomain}
                onDomainUpdated={handleDomainUpdated}
                onDomainDeleted={handleDomainDeleted}
            />
        </Wrapper>
    );
};

export default CustomDomainSection;
